import React, { useCallback, useRef, useState } from 'react';
import { Descope, useSession } from '@descope/react-sdk';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { PrimeReactButton as ButtonAtom } from 'design-system/my-lib';
import { validateReturnUrl } from '../utils/signInHandoff';
import { useDescopeTheme } from '../hooks/useDescopeTheme';
import './LoginPage.scss';

const DESCOPE_FLOW_ID = 'sign-up-or-in';
// Must match STYLE_ID in scripts/descope/lib/theme-from-tokens.js — the named Descope style
// this repo owns (token-derived colors/typography/spacing/radius + the input-wrapper pill
// radius override), distinct from the project's untouched default style. See
// descope/README.md § Theme.
const DESCOPE_STYLE_ID = 'center-design-system';

type DescopeSuccessEvent = Parameters<NonNullable<React.ComponentProps<typeof Descope>['onSuccess']>>[0];

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logAnalyticsEvent = useAnalytics();
  const { isSessionLoading } = useSession();
  const descopeTheme = useDescopeTheme();
  const [flowError, setFlowError] = useState(false);
  const [flowKey, setFlowKey] = useState(0);
  const flowContainerRef = useRef<HTMLDivElement>(null);

  const returnUrl = validateReturnUrl(searchParams.get('returnUrl')) ?? '/';

  const handleSuccess = useCallback(
    (event: DescopeSuccessEvent) => {
      logAnalyticsEvent(event.detail?.firstSeen ? 'user_signed_up' : 'user_signed_in');
      navigate(returnUrl, { replace: true });
    },
    [logAnalyticsEvent, navigate, returnUrl]
  );

  const handleError = useCallback(() => {
    setFlowError(true);
  }, []);

  const handleRetry = useCallback(() => {
    setFlowError(false);
    setFlowKey((key) => key + 1);
  }, []);

  React.useEffect(() => {
    if (!flowError) {
      flowContainerRef.current?.focus();
    }
  }, [flowError, flowKey]);

  const renderPanel = () => {
    if (isSessionLoading) {
      return (
        <div className="login-page__skeleton" data-testid="login-page-skeleton">
          <Skeleton width="100%" height="40px" className="login-page__skeleton-row" />
          <Skeleton width="100%" height="40px" className="login-page__skeleton-row" />
          <Skeleton width="60%" height="40px" />
        </div>
      );
    }
    if (flowError) {
      return (
        <div className="login-page__error" role="alert">
          <p className="login-page__error-title">{t('auth.login.flowErrorTitle')}</p>
          <p className="login-page__error-message">{t('auth.login.flowErrorMessage')}</p>
          <ButtonAtom onClick={handleRetry}>{t('auth.login.retryButton')}</ButtonAtom>
        </div>
      );
    }
    return (
      <div className="login-page__flow" ref={flowContainerRef} tabIndex={-1}>
        <Descope
          key={flowKey}
          flowId={DESCOPE_FLOW_ID}
          theme={descopeTheme}
          styleId={DESCOPE_STYLE_ID}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </div>
    );
  };

  return (
    <main className="login-page">
      <div className="login-page__panel">{renderPanel()}</div>
    </main>
  );
};

export default LoginPage;
